// File: src/components/feeds/AnnouncementFeed/AnnouncementFeed.tsx

import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useSwipeable } from "react-swipeable";
import { Announcement, AnnouncementType } from "./AnnouncementCard";
import { AnnouncementCard } from "./AnnouncementCard";
import { Flex, Typography, Button } from "../../../components";

export interface AnnouncementFeedProps {
  announcements: Announcement[];
  onDismiss?: (id: string) => void;
  onUndo?: (id: string) => void;
}

const FeedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  width: 100%;

  .fade-enter {
    opacity: 0;
    transform: translateY(10px);
  }
  .fade-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 300ms, transform 300ms;
  }
  .fade-exit {
    opacity: 1;
    transform: translateY(0);
  }
  .fade-exit-active {
    opacity: 0;
    transform: translateY(-10px);
    transition: opacity 250ms, transform 250ms;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0;
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const UndoBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

interface Props {
    item: Announcement;
    onDismiss: (id: string) => void;
  }
export const DismissibleAnnouncement: React.FC<Props> = ({ item, onDismiss }) => {
    const handlers = useSwipeable({
      onSwipedLeft: () => item.dismissible && onDismiss(item.id),
      delta: 40,
      preventScrollOnSwipe: true,
      trackTouch: true,
    });
  
    return (
      <div {...handlers}>
        <AnnouncementCard item={item} onDismiss={onDismiss} />
      </div>
    );
  };

  export const AnnouncementFeed: React.FC<AnnouncementFeedProps> = ({
    announcements: initialAnnouncements,
    onDismiss,
    onUndo,
  }) => {
    const [announcements, setAnnouncements] = useState(initialAnnouncements);
    const [dismissedStack, setDismissedStack] = useState<Announcement[]>([]);
  
    const handleDismiss = useCallback(
      (id: string) => {
        const item = announcements.find((a) => a.id === id);
        if (!item) return;
  
        setAnnouncements((prev) => prev.filter((a) => a.id !== id));
        setDismissedStack((prev) => [item, ...prev]); // push to top
        onDismiss?.(id);
      },
      [announcements, onDismiss]
    );
  
    const handleUndo = () => {
      if (dismissedStack.length === 0) return;
  
      const [latest, ...rest] = dismissedStack;
      setAnnouncements((prev) => [latest, ...prev]);
      setDismissedStack(rest);
      onUndo?.(latest.id);
    };
  
    return (
      <FeedWrapper>
        {announcements
            .map((item) => (
                <DismissibleAnnouncement key={item.id} item={item} onDismiss={handleDismiss} />
            ))}
  
        {dismissedStack.length > 0 && (
          <UndoBar>
            <Typography variant="caption" colorScheme="muted">
              Dismissed: <strong>{dismissedStack[0].message}</strong>
            </Typography>
            <Button variant="text" size="compact" onClick={handleUndo}>
              Undo
            </Button>
          </UndoBar>
        )}
      </FeedWrapper>
    );
  };

